import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const BRAND = "The Good Life";
const CONTACT_EMAIL = "Halegalcannabis@gmail.com";
const CONTACT_PHONE = "828-424-4828";
const LOCATION = "Asheville, NC";

function PolicyLayout({ title, updated, children }) {
  return (
    <div className="mx-auto max-w-3xl px-6 py-16" data-testid="policy-page">
      <Link
        to="/"
        className="mb-8 inline-flex items-center gap-2 text-sm text-neutral-600 transition-colors hover:text-[#a06d34]"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back home
      </Link>
      <p className="overline mb-2">Legal</p>
      <h1 className="mb-2 text-4xl font-black text-[#2b2520]">{title}</h1>
      {updated && <p className="mb-8 text-xs uppercase tracking-widest text-neutral-500">Last Updated: {updated}</p>}
      <div className="policy-body space-y-5 text-[15px] leading-relaxed text-neutral-700">{children}</div>
      <div className="mt-12 border-t border-black/10 pt-6 text-sm text-neutral-600">
        <p className="font-medium text-[#2b2520]">Questions?</p>
        <p>Email: {CONTACT_EMAIL} · Phone: {CONTACT_PHONE}</p>
        <p>{BRAND} — {LOCATION}</p>
      </div>
    </div>
  );
}

const H = ({ children }) => <h2 className="pt-4 text-lg font-bold text-[#2b2520]">{children}</h2>;
const Sub = ({ children }) => <h3 className="pt-2 font-semibold text-[#2b2520]">{children}</h3>;
const P = ({ children }) => <p>{children}</p>;
const UL = ({ items }) => (
  <ul className="list-disc space-y-1 pl-6">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

export function PrivacyPolicy() {
  return (
    <PolicyLayout title="Privacy Policy">
      <P>
        We are committed to protecting your privacy in compliance with applicable laws, including North Carolina
        consumer protection regulations. Note that as of 2026, hemp-derived products (typically under 0.3% Delta-9 THC)
        are regulated under federal and state guidelines, but we do not collect or process sensitive health data beyond
        what's necessary for age verification and transactions.
      </P>
      <P>
        By using our Services, you agree to the collection and use of information in accordance with this policy. If you
        do not agree, please do not use our Services.
      </P>
      <H>1. Information We Collect</H>
      <P>We collect the following types of information:</P>
      <Sub>Information You Provide Directly</Sub>
      <UL
        items={[
          "Name, email address, phone number, shipping/billing address when placing orders, creating an account, or contacting us.",
          "Payment information (handled securely by third-party processors; we do not store full card details).",
          "Date of birth or age verification details (required for compliance with age restrictions—must be 21+ for purchases).",
          "Government-issued ID information (e.g., driver's license number, expiration) for age verification where required by law or policy.",
          "Any messages, inquiries, or feedback you submit.",
        ]}
      />
      <Sub>Automatically Collected Information</Sub>
      <UL
        items={[
          "Device and browser details (IP address, browser type, operating system).",
          "Usage data (pages visited, time spent, referring site).",
          "Cookies and similar technologies for site functionality, analytics, and preferences (see our Cookie Policy section below).",
        ]}
      />
      <Sub>In-Store Information</Sub>
      <UL
        items={[
          "Security camera footage in and around our retail location for safety and loss prevention.",
          "Purchase history and transaction details.",
        ]}
      />
      <P>
        We do not intentionally collect sensitive personal information (e.g., health data, precise geolocation beyond
        IP-derived area, or biometric data) unless directly provided for a specific purpose and required for compliance.
      </P>
      <H>2. How We Use Your Information</H>
      <P>We use collected information to:</P>
      <UL
        items={[
          "Process and fulfill orders, including shipping and payment.",
          "Verify age and eligibility to purchase restricted products.",
          "Provide customer service, respond to inquiries, and handle refunds/exchanges per our Refund Policy.",
          "Improve our Site, products, and services (e.g., analytics on usage patterns).",
          "Send transactional emails (order confirmations, shipping updates).",
          "Comply with legal obligations, prevent fraud, enforce our policies, or respond to subpoenas/law enforcement requests.",
          "With your consent, send promotional offers or newsletters (you can opt out anytime).",
        ]}
      />
      <H>3. How We Share Your Information</H>
      <P>We do not sell your personal information. We may share it with:</P>
      <UL
        items={[
          "Service providers (e.g., payment processors like Stripe/PayPal, shipping carriers like USPS/UPS, email services, website hosting/analytics tools like Google Analytics).",
          "Legal authorities if required by law, court order, or to protect our rights/safety.",
          "In the event of a business transfer (e.g., merger, acquisition), your information may transfer to the new entity.",
        ]}
      />
      <H>4. Data Security</H>
      <P>
        We implement reasonable administrative, technical, and physical safeguards to protect your information from
        unauthorized access, loss, or misuse. However, no method of transmission over the internet or electronic storage
        is 100% secure. We cannot guarantee absolute security.
      </P>
      <H>5. Cookies and Tracking Technologies</H>
      <P>
        We use cookies, pixels, and similar tools for essential functions (e.g., cart persistence), performance
        (analytics), and marketing. You can manage preferences via your browser settings or our cookie banner (if
        implemented). Third-party tools (e.g., Google Analytics) may collect data; see their policies for details.
      </P>
      <H>6. Your Rights and Choices</H>
      <P>Depending on applicable laws:</P>
      <UL
        items={[
          "Access, correct, or delete your personal information by contacting us.",
          "Opt out of marketing emails (unsubscribe link in emails or contact us).",
          "Limit certain automated tracking (browser Do Not Track signals may not be fully honored).",
          "For North Carolina residents, while no comprehensive state privacy law like CCPA applies broadly to small businesses as of 2026, we aim to handle requests reasonably.",
        ]}
      />
      <H>7. Retention</H>
      <P>
        We retain information as long as needed for the purposes described (e.g., transaction records for
        accounting/tax, typically 7 years) or as required by law, then securely delete or anonymize it.
      </P>
      <H>8. Third-Party Links</H>
      <P>
        Our Site may link to third-party sites (e.g., social media, payment gateways). We are not responsible for their
        privacy practices—review their policies.
      </P>
      <H>9. Changes to This Policy</H>
      <P>
        We may update this Privacy Policy. Changes will be posted here with an updated "Last Updated" date. Continued use
        after changes constitutes acceptance.
      </P>
    </PolicyLayout>
  );
}

export function RefundPolicy() {
  return (
    <PolicyLayout title="Refund Policy">
      <P>
        This Refund Policy applies to all purchases made from {BRAND} (the "Company," "we," "us," or "our"), including
        in-store purchases and any online or delivery services for hemp-derived cannabinoid products, accessories,
        devices, or related items in compliance with applicable North Carolina laws.
      </P>
      <P>
        We strive for customer satisfaction, but due to the nature of cannabis/hemp products (including regulations
        around consumables, hygiene, and potential impairment), our refund policy is limited. All decisions regarding
        refunds or exchanges are at our sole discretion and subject to verification.
      </P>
      <H>1. Eligible Items for Refund – Devices and Non-Consumable Products</H>
      <P>
        If any device (e.g., vaporizers, batteries, grinders, pipes, or other hardware/accessories) or non-consumable
        item is defective, damaged, or broken upon receipt or use:
      </P>
      <UL
        items={[
          "You may request a full refund or exchange.",
          "You must return the item in person to our store location within 3 days (72 hours) of the original purchase date/time.",
          "Proof of purchase (receipt or digital record) is required.",
          "The item must be returned in its original condition/packaging (where applicable), with any defects clearly demonstrable.",
          "We will inspect the item to confirm the issue (e.g., manufacturing defect or breakage not caused by misuse).",
          "If approved, refunds will be issued to the original payment method.",
        ]}
      />
      <H>2. Non-Refundable Items</H>
      <P>
        The following products are non-refundable and non-exchangeable under any circumstances (including defects,
        dissatisfaction, or change of mind):
      </P>
      <UL
        items={[
          "All flower (bud, hemp flower, etc.)",
          "All pre-rolls",
          "All edibles (gummies, chocolates, beverages, etc.)",
          "Any other consumable hemp-derived cannabinoid products (e.g., tinctures, capsules, topicals, unless specifically noted otherwise)",
        ]}
      />
      <P>
        This policy exists due to health, safety, and regulatory requirements surrounding consumable cannabis/hemp
        products.
      </P>
      <H>Questions or Concerns?</H>
      <P>
        If you have questions about this Refund Policy or need to request a refund for an eligible item, please contact
        us at: {CONTACT_EMAIL} or {CONTACT_PHONE}.
      </P>
      <P>
        For in-store returns, please visit us at our {LOCATION} location with your receipt and the item in question.
      </P>
    </PolicyLayout>
  );
}

export function SalesPolicy() {
  return (
    <PolicyLayout title="Sales Policy">
      <H>Return & Refund Policy — All Sales Final</H>
      <P>All sales are final. No returns, refunds, or exchanges.</P>
      <UL
        items={[
          `All sales made through ${BRAND}, whether in-store or online, are final.`,
          "We do not accept returns, refunds, or exchanges for any products once a purchase has been completed.",
          "Please review your order carefully before completing your purchase. By completing a transaction, you acknowledge and agree to this policy.",
          "This policy applies to all products, including promotional items and discounted merchandise.",
          "Exceptions may only be made if required by law.",
        ]}
      />
      <H>Important Notice</H>
      <P>
        By making a purchase on our website or in our stores, you confirm that you have read, understood, and agree to be
        bound by this Sales Policy. If you do not agree with this policy, please do not complete your purchase.
      </P>
    </PolicyLayout>
  );
}

export function CompliancePolicy() {
  return (
    <PolicyLayout title="Compliance Policy" updated="January 2025">
      <H>1. Scope and Commitment</H>
      <P>{BRAND} is dedicated to operating lawfully and responsibly. We will:</P>
      <UL
        items={[
          "Comply with the 2018 Farm Bill and applicable federal laws.",
          "Adhere to North Carolina statutes (e.g., G.S. 90-87 et seq. on controlled substances and hemp exclusions).",
          "Implement internal controls for age verification, product safety, accurate labeling, and record-keeping.",
          "Train all employees on compliance requirements annually or upon material changes.",
        ]}
      />
      <H>2. Age Verification and Sales Restrictions</H>
      <UL
        items={[
          "All purchasers must be at least 21 years of age.",
          "We require valid government-issued photo ID (e.g., driver's license) for every in-person or delivery transaction involving restricted products.",
          "We will not sell to anyone appearing under 21 or unable to provide valid ID.",
          "No sales to minors; violations may result in immediate termination and reporting to authorities.",
          "Products are for personal use only by adults; resale or distribution prohibited.",
        ]}
      />
      <H>3. Product Compliance</H>
      <UL
        items={[
          "All products are hemp-derived and represented as containing no more than 0.3% Delta-9 THC on a dry weight basis (current federal/state standard).",
          "We source from suppliers providing Certificates of Analysis (COAs) from ISO-accredited labs confirming compliance, including potency, contaminants (pesticides, heavy metals, microbes), and no prohibited synthetics.",
          "No medical claims: Products are not intended to diagnose, treat, cure, or prevent any disease.",
        ]}
      />
      <H>4. Labeling and Packaging Requirements</H>
      <P>All products include:</P>
      <UL
        items={[
          "Accurate ingredient list, cannabinoid profile, and potency (mg per serving/total).",
          '"Contains hemp-derived cannabinoids. For use by adults 21+ only."',
          '"Keep out of reach of children and pets." "May cause impairment—do not operate vehicles/machinery."',
          "QR code/link to COA.",
          "Child-resistant packaging for edibles, pre-rolls, and other consumables.",
          "Labels comply with FTC guidelines (no false advertising) and any applicable state/federal standards.",
        ]}
      />
      <H>5. Testing and Quality Assurance</H>
      <UL
        items={[
          "Third-party lab testing required for potency, contaminants, and compliance before sale.",
          "Records of COAs maintained for at least 2 years (or longer if required).",
          "Any non-compliant product immediately quarantined, removed from sale, and reported/disposed per law.",
        ]}
      />
      <H>6. Record-Keeping and Tracking</H>
      <UL
        items={[
          "Maintain detailed records of purchases, sales, inventory, suppliers, and testing for audit purposes.",
          "If state tracking system implemented, we will participate fully.",
        ]}
      />
      <H>7. Advertising and Marketing</H>
      <UL
        items={[
          "No targeting minors or implying medical benefits.",
          'All ads include "21+ only" disclaimers.',
          "Compliant with platform rules.",
        ]}
      />
      <H>8. Enforcement and Violations</H>
      <UL
        items={[
          "Internal audits conducted regularly.",
          "Violations of this policy result in disciplinary action up to termination.",
        ]}
      />
      <H>9. Monitoring and Updates</H>
      <P>
        This policy is reviewed at least quarterly or upon legal changes. Updates posted with new "Last Updated" date.
      </P>
    </PolicyLayout>
  );
}

export function TermsConditions() {
  return (
    <PolicyLayout title="Terms & Conditions" updated="June 2026">
      <P>
        Welcome to {BRAND}. These Terms & Conditions ("Terms") govern your access to and use of our website, products,
        and services (collectively, the "Services"). By accessing our Site, creating an account, or placing an order,
        you agree to be bound by these Terms. If you do not agree, please do not use our Services.
      </P>
      <H>1. Eligibility &amp; Age Requirement</H>
      <P>
        You must be at least 21 years of age to create an account, browse restricted products, or make a purchase. By
        using the Services, you represent and warrant that you are 21 or older and that all information you provide is
        accurate. We require valid government-issued photo ID and age verification at signup, and we reserve the right
        to refuse or cancel any order that cannot be verified.
      </P>
      <H>2. Products &amp; Compliance</H>
      <P>
        All products sold are hemp-derived and represented as containing no more than 0.3% Delta-9 THC on a dry weight
        basis, in accordance with the 2018 Farm Bill and applicable federal and state law. Products are intended for
        personal use only by adults 21+. Resale or redistribution is prohibited. Our products are not intended to
        diagnose, treat, cure, or prevent any disease, and no medical claims are made.
      </P>
      <H>3. Orders, Pricing &amp; Payment</H>
      <UL
        items={[
          "All prices are listed in U.S. dollars and are subject to change without notice.",
          "We reserve the right to limit quantities, refuse, or cancel any order at our discretion.",
          "Payment is processed securely by third-party processors; we do not store full card details.",
          "You are responsible for providing accurate shipping and billing information.",
        ]}
      />
      <H>4. Shipping</H>
      <P>
        We ship only to jurisdictions where our products are legal. An adult 21+ signature may be required upon
        delivery. Title and risk of loss pass to you upon delivery to the carrier. We are not responsible for delays
        caused by carriers or events outside our control.
      </P>
      <H>5. Sales &amp; Refunds</H>
      <P>
        All sales are final. Consumable products (flower, pre-rolls, edibles, and similar) are non-refundable and
        non-exchangeable. Please review our Sales Policy and Refund Policy for full details, including limited
        exceptions for defective non-consumable devices.
      </P>
      <H>6. Accounts</H>
      <P>
        You are responsible for maintaining the confidentiality of your account credentials and for all activity under
        your account. Notify us immediately of any unauthorized use. We may suspend or terminate accounts that violate
        these Terms or applicable law.
      </P>
      <H>7. Acceptable Use</H>
      <P>
        You agree not to misuse the Services, including by attempting to circumvent age verification, providing false
        information, engaging in fraud, or using the Site for any unlawful purpose.
      </P>
      <H>8. Intellectual Property</H>
      <P>
        All content on the Site, including text, graphics, logos, and images, is the property of {BRAND} or its
        licensors and is protected by applicable intellectual property laws. You may not reproduce or distribute it
        without permission.
      </P>
      <H>9. Disclaimers &amp; Limitation of Liability</H>
      <P>
        The Services are provided "as is" without warranties of any kind. To the fullest extent permitted by law,
        {" "}{BRAND} shall not be liable for any indirect, incidental, or consequential damages arising from your use of
        the Services or products.
      </P>
      <H>10. Governing Law</H>
      <P>
        These Terms are governed by the laws of the State of North Carolina, without regard to conflict-of-law principles. Any
        disputes shall be resolved in the courts located in or nearest to {LOCATION}.
      </P>
      <H>11. Changes to These Terms</H>
      <P>
        We may update these Terms from time to time. Changes will be posted here with an updated "Last Updated" date.
        Continued use after changes constitutes acceptance.
      </P>
      <H>12. Contact</H>
      <P>
        Questions about these Terms? Contact us at {CONTACT_EMAIL} or {CONTACT_PHONE}.
      </P>
    </PolicyLayout>
  );
}
